import { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  SequenceStore,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { PlayButton } from '~/ui/buttons';
import { select } from '~/lib/select';
import { shallow } from 'zustand/shallow';
import {
  MediaPlayerProvider,
  useMediaPlayerStore
} from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { AudioProgress } from '../audio-popup/components/AudioProgress';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import {
  GlobalMediaStore,
  useGlobalMediaStore
} from '~/ui/video-player/GlobalMediaProvider';
import { HORIZONTAL_AUDIO_PLAYER } from '../itemIds';
import { Item, registry, RenderProps } from '../Item';
import { AudioPlayer } from '~/ui/video-player/AudioPlayer';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';

interface Props {
  mediaUrl: string;
  name: string;
  body: string;
  subtitlesUrl: string;
  blocking?: boolean;
  isolated?: boolean;
}

const selector = (s: MediaPlayerStore) =>
  select(s, 'loadMedia', 'status', 'play', 'pause');

const seqSelector = (s: SequenceStore) =>
  select(s, 'setAudioPlayed', 'addBlocker', 'removeBlocker');

interface AudioPlayerProps {
  mediaUrl: string;
  subtitlesUrl?: string;
  name: string;
  body: string;
}

const HorizontalAudioPlayerElement = ({
  mediaUrl,
  subtitlesUrl,
  name,
  body
}: AudioPlayerProps) => {
  const { loadMedia, status, pause, play } = useMediaPlayerStore(
    selector,
    shallow
  );
  const { addBlocker, removeBlocker, setAudioPlayed } = useSequenceStore(
    seqSelector,
    shallow
  );
  const [isPaused, setIsPaused] = useState(false);

  useGlobalMedia();

  const globalMedia = useGlobalMediaStore() as GlobalMediaStore;
  const globalPlayer = globalMedia?.player;

  const ref = useRef<HTMLDivElement>();

  useEffect(() => {
    loadMedia(mediaUrl, subtitlesUrl, 'audio', false, 0);
  }, []);

  useEffect(() => {
    if (status === 'done') {
      setAudioPlayed(mediaUrl);
    }
  }, [status]);

  useEffect(() => {
    if (globalPlayer) {
      globalPlayer.pause();
    }
  }, []);

  useEffect(() => {
    if (status != 'done' && isPaused) {
      pause();
    }
  }, [status, open]);

  useEffect(() => {
    addBlocker(mediaUrl);

    if (status == 'nomedia') {
      removeBlocker(mediaUrl);
    }
  }, [status]);

  useEffect(() => {
    if ((mediaUrl && status === 'done') || !mediaUrl) {
      removeBlocker(mediaUrl);
    }
  }, [status, open, mediaUrl]);

  const clickHandler = () => {
    if (globalPlayer) {
      setIsPaused(true);
      globalPlayer.pause();
      return;
    }

    setIsPaused(false);
    play();
  };

  const getContentForPlayer = (name: string, body: string) => {
    if (name && body == '') {
      return <x.span gridArea="title">{name}</x.span>;
    } else {
      return (
        <x.span gridArea="title">
          <x.h3 mb="15px">{name ? name : 'AudioRecording01.wav'}</x.h3>
          <div dangerouslySetInnerHTML={{ __html: body }} />
        </x.span>
      );
    }
  };

  return (
    <x.div
      w="100%"
      p="md"
      onClick={e => e.stopPropagation()}
      display="grid"
      gridTemplateColumns="auto 1fr"
      gridTemplateAreas="'button title' 'button progress'"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
    >
      {getContentForPlayer(name, body)}
      {mediaUrl && (
        <>
          <AudioPlayer />
          <x.div mr="15px" gridArea="button">
            <PlayButton
              state={status === 'playing' ? 'playing' : 'paused'}
              onClick={() => clickHandler()}
            />
          </x.div>
          <x.div gridArea="progress">
            <AudioProgress w="100%" />
          </x.div>
        </>
      )}
    </x.div>
  );
};

export class HorizontalAudioPlayer extends Item<
  typeof HORIZONTAL_AUDIO_PLAYER,
  Props
> {
  protected render({ mediaUrl, name, body, subtitlesUrl }: RenderProps<Props>) {
    return (
      <MediaPlayerProvider>
        <HorizontalAudioPlayerElement
          mediaUrl={mediaUrl}
          subtitlesUrl={subtitlesUrl}
          name={name}
          body={body}
        />
        {ReactDOM.createPortal(
          <>
            <MediaCaptions />
          </>,
          document.getElementById('root')!
        )}
      </MediaPlayerProvider>
    );
  }
}

registry[HORIZONTAL_AUDIO_PLAYER] = HorizontalAudioPlayer;
